import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { UserCog, Info, Shield, Loader2, Phone, Mail } from 'lucide-react';

export const UserFormPanel = ({ 
  user, 
  roles = [],
  isOpen, 
  onClose, 
  onSubmit 
}) => {
  const { t } = useTranslation('security');
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    status: 'active',
    role_ids: []
  });

  useEffect(() => {
    if (isOpen) {
      if (user) {
        setFormData({
          email: user.email || '',
          password: '',
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          phone: user.phone || '',
          status: user.status || 'active',
          role_ids: user.role_ids || user.roles?.map(r => r.id) || []
        });
      } else {
        setFormData({
          email: '',
          password: '',
          first_name: '',
          last_name: '',
          phone: '',
          status: 'active',
          role_ids: []
        });
      }
      setActiveTab('basic');
    }
  }, [user, isOpen]);

  const handleRoleToggle = (roleId) => {
    const currentRoles = formData.role_ids || [];
    if (currentRoles.includes(roleId)) {
      setFormData({...formData, role_ids: currentRoles.filter(id => id !== roleId)});
    } else {
      setFormData({...formData, role_ids: [...currentRoles, roleId]});
    }
  };

  const handleSubmit = async () => {
    if (!formData.first_name || !formData.last_name || !formData.email) return;
    if (!user && !formData.password) return;
    
    setLoading(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error saving user:', error);
    }
    setLoading(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-hidden flex flex-col p-0">
        <SheetHeader className="px-6 py-4 border-b bg-gradient-to-r from-[#E63946]/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-[#E63946]/10 flex items-center justify-center">
              <UserCog className="h-6 w-6 text-[#E63946]" />
            </div>
            <div>
              <SheetTitle className="text-lg font-semibold">
                {user?.id ? t('users.form.editTitle') : t('users.form.title')}
              </SheetTitle>
              <SheetDescription>
                {user?.id ? t('users.form.editSubtitle') : t('users.form.subtitle')}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="px-6 py-2 border-b justify-start rounded-none bg-transparent">
            <TabsTrigger value="basic" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Info className="h-4 w-4 mr-1" />
              {t('users.form.tabBasic')}
            </TabsTrigger>
            <TabsTrigger value="roles" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Shield className="h-4 w-4 mr-1" />
              {t('users.form.tabRoles')}
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1">
            <TabsContent value="basic" className="p-6 space-y-4 mt-0">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('users.form.firstName')} *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    placeholder={t('users.form.firstNamePlaceholder')}
                    data-testid="user-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('users.form.lastName')} *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    placeholder={t('users.form.lastNamePlaceholder')}
                    data-testid="user-last-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('users.form.email')} *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder={t('users.form.emailPlaceholder')}
                    className="pl-9"
                    data-testid="user-email"
                  />
                </div>
              </div>

              {!user && (
                <div className="space-y-2">
                  <Label>{t('users.form.password')} *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder={t('users.form.passwordPlaceholder')}
                    data-testid="user-password"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('users.form.phone')}</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder={t('users.form.phonePlaceholder')}
                      className="pl-9"
                      data-testid="user-phone"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('users.form.status')}</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({...formData, status: value})}
                  >
                    <SelectTrigger data-testid="user-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">{t('users.status.active')}</SelectItem>
                      <SelectItem value="inactive">{t('users.status.inactive')}</SelectItem>
                      <SelectItem value="suspended">{t('users.status.suspended')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="roles" className="p-6 space-y-4 mt-0">
              <div className="space-y-2">
                <Label>{t('users.form.roles')}</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  {t('users.form.rolesDescription')}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {roles.map((role) => (
                    <div 
                      key={role.id} 
                      className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        (formData.role_ids || []).includes(role.id)
                          ? 'border-[#E63946] bg-[#E63946]/5'
                          : 'border-border hover:border-[#E63946]/50'
                      }`}
                      onClick={() => handleRoleToggle(role.id)}
                    >
                      <Checkbox
                        id={role.id}
                        checked={(formData.role_ids || []).includes(role.id)}
                        onCheckedChange={() => handleRoleToggle(role.id)}
                        data-testid={`role-checkbox-${role.id}`}
                      />
                      <div>
                        <label
                          htmlFor={role.id}
                          className="text-sm font-medium leading-none cursor-pointer"
                        >
                          {role.name}
                        </label>
                        {role.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {role.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>

        <div className="px-6 py-4 border-t bg-muted/30 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common:cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !formData.first_name || !formData.last_name || !formData.email || (!user && !formData.password)} 
            className="bg-[#E63946] hover:bg-[#d32836]"
            data-testid="save-user-btn"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {user?.id ? t('users.form.update') : t('users.form.save')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default UserFormPanel;
